import { ReactElement, useEffect, useRef, useState } from 'react';
import { Drawer, Modal, Rating, Text } from '@mantine/core';
import { useMediaQuery } from 'react-responsive';
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import useCurrentUser from 'hooks/useCurrentUser';
import { BreakpointMedia } from '../../../utils/config';
import { Product } from '../../../models/product.types';
import {
  useCreateProductReview,
  useUploadReviewImage,
} from '../../../services/product/product.queries';
import { heapTrackReviewSubmitted } from '../../../services/heap/heap';
import styles from './WriteReviewModal.module.scss';
import { ReviewCustomFieldsForm, ReviewCustomFieldsFormHandle } from './ReviewCustomFieldsForm';

type Props = {
  opened: boolean;
  onClose: () => void;
  product?: Product;
};

export function WriteReviewModal({
  opened,
  onClose,
  product,
}: Props): ReactElement {
  const reviewTitleRef = useRef<HTMLInputElement>(null);
  const reviewBodyRef = useRef<HTMLTextAreaElement>(null);
  const customFieldsRef = useRef<ReviewCustomFieldsFormHandle>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const [rating, setRating] = useState(0);
  const [submittedProductIds, setSubmittedProductIds] = useState<Set<number>>(new Set());

  const [errors, setErrors] = useState<{
    rating?: string;
    reviewTitle?: string;
    reviewBody?: string
  }>({});

  const isMobile = !useMediaQuery({ query: BreakpointMedia.MIN_MD });

  useEffect(() => {
    if (opened) {
      setRating(0);
      setSelectedFiles([]);
      setErrors({});
    }
  }, [opened]);

  useEffect(() => {
    if (isMobile && opened) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, opened]);

  const { currentUser } = useCurrentUser();

  const createProductReviewMutation = useCreateProductReview();
  const uploadReviewImageMutation = useUploadReviewImage();

  const isSubmitted = !!product?.id && submittedProductIds.has(product.id);

  const handleSubmitReview = async () => {
    const reviewTitle = reviewTitleRef.current?.value ?? '';
    const reviewBody = reviewBodyRef.current?.value ?? '';

    const newErrors: typeof errors = {};
    if (!rating) newErrors.rating = 'Rating needs to be filled';
    if (!reviewTitle) newErrors.reviewTitle = 'Review title needs to be filled';
    if (!reviewBody) newErrors.reviewBody = 'Review body needs to be filled';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    if (!product?.id) {
      console.error('Product ID is missing');
      return;
    }

    const response = await createProductReviewMutation.mutateAsync({
      productId: product?.id,
      reviewScore: rating,
      reviewTitle,
      reviewBody,
      displayName: currentUser.display_name,
      email: currentUser.email,
      skuId: product.skus[0].id,
      productTitle: product.title,
      productUrl: window.location.href,
      totalMediaAttached: selectedFiles.length,
      customFields: customFieldsRef.current?.getPayload(),
    });

    if (response?.image_upload_token && selectedFiles.length > 0) {
      await Promise.all(
        selectedFiles.map((image) => uploadReviewImageMutation.mutateAsync({
          productId: product.id,
          imageUploadToken: response.image_upload_token,
          image,
        })),
      );
    }

    heapTrackReviewSubmitted({
      productId: product.id,
      skuId: product.skus[0].id,
      userId: currentUser.id,
      createdAt: new Date().toISOString(),
      starRating: rating,
      totalMediaAttached: selectedFiles.length,
    });

    setSubmittedProductIds((prev) => new Set(prev).add(product.id));
  };

  const feedbackText = 'Your feedback helps us improve recipes. It\'ll take less than 30 seconds to submit.';

  const content = (
    <div className={styles.scrollable_content}>
      {isMobile && (
        <>
          <h1 className={styles.drawer_title}>Review Item</h1>
          <p className={styles.drawer_feedback_text}>{feedbackText}</p>
        </>
      )}
      {product && (() => {
        const sku = product.skus[0];
        return (
          <div className={styles.product_info}>
            <img
              src={sku.images[0].image.url}
              alt={product.image_alt}
              className={styles.product_image}
            />
            <div className={styles.product_details}>
              <strong className={styles.title}>{product.title}</strong>
              {sku && (
                <span className={styles.sku_info}>
                  <strong className={styles.sku_price}>{`$${sku.price}`}</strong>
                  {' • '}
                  {[
                    sku.servings && `$${sku.subscription_price_per_serving} / serving`,
                    sku.servings && `Feeds ${sku.servings}`,
                    sku.prep_and_cook_time && `${sku.prep_and_cook_time} min ${sku.product_type_name}`,
                  ].filter(Boolean)
                    .join(' • ')}
                </span>
              )}
            </div>
          </div>
        );
      })()}
      <h2 className={styles.h2}>How was your overall experience with this item?</h2>
      <Rating
        size="lg"
        value={rating}
        onChange={setRating}
      />
      {errors.rating && <span className={styles.error_text}>{errors.rating}</span>}
      <h2 className={styles.h2}>Review Title</h2>
      <input
        ref={reviewTitleRef}
        type="text"
        className={`${styles.review_title_input}${errors.reviewTitle ? ` ${styles.input_error}` : ''}`}
      />
      <h2 className={styles.h2}>Write your Review below:</h2>
      <textarea
        ref={reviewBodyRef}
        className={`${styles.review_body_input}${errors.reviewBody ? ` ${styles.input_error}` : ''}`}
      />
      <Dropzone
        onDrop={(files) => setSelectedFiles((prev) => [...prev, ...files].slice(0, 3))}
        maxSize={5 * 1024 ** 2}
        accept={IMAGE_MIME_TYPE}
        multiple
        disabled={selectedFiles.length >= 3}
        className={styles.image_upload}
      >
        <Dropzone.Idle>
          <p className={styles.image_upload_title}>
            Drag images here or click to select files
          </p>
          <p className={styles.image_upload_subtitle}>
            Attach up to 3 images. Each should not exceed 5mb.
          </p>
        </Dropzone.Idle>
        <Dropzone.Accept>
          <Text className={styles.image_upload_title}>Drop images here</Text>
        </Dropzone.Accept>
        <Dropzone.Reject>
          <Text className={styles.image_upload_title} color="red">
            Images only, max 5mb each
          </Text>
        </Dropzone.Reject>
      </Dropzone>
      <div>
        {selectedFiles.length > 0 && (
        <p className={styles.image_upload_title}>
          {`Uploaded Photos (${selectedFiles.length})`}
        </p>
        )}
        {[0, 1, 2].map((index) => {
          const file = selectedFiles[index];
          return file && (
            <div key={file.name} className={styles.file_row}>
              <img
                src={URL.createObjectURL(file)}
                alt={file.name}
                className={styles.file_image}
              />
              <div className={styles.file_info}>
                <span className={styles.file_name}>{file.name}</span>
                <span className={styles.file_upload_complete}>Upload Complete</span>
              </div>
              <button
                type="button"
                onClick={() => setSelectedFiles((prev) => prev.filter((_, i) => i !== index))}
                className={styles.remove_file_button}
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>
      <ReviewCustomFieldsForm ref={customFieldsRef} />
    </div>
  );

  const submitButton = (
    <button
      type="button"
      onClick={handleSubmitReview}
      disabled={isSubmitted || createProductReviewMutation.isPending}
      className={isSubmitted ? styles.submit_button_submitted : styles.submit_button}
    >
      {isSubmitted ? '✓ Your Review has been Submitted. Thank you for your feedback!' : 'Submit Review'}
    </button>
  );

  if (isMobile) {
    return (
      <Drawer
        opened={opened}
        onClose={onClose}
        position="bottom"
        overlayBlur={6}
        overlayOpacity={0.6}
        size="90%"
        lockScroll
        classNames={{
          body: styles.modal_body,
          closeButton: styles.modal_close,
        }}
        styles={{
          drawer: {
            display: 'flex',
            flexDirection: 'column',
          },
          header: {
            marginBottom: 0,
          },
          body: {
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          },
        }}
      >
        {content}
        {submitButton}
      </Drawer>
    );
  }

  return (
    <Modal
      title="Review Item"
      opened={opened}
      onClose={onClose}
      centered
      overlayBlur={6}
      overlayOpacity={0.6}
      size={620}
      classNames={{
        modal: styles.modal,
        body: styles.modal_body,
        title: styles.modal_title,
        close: styles.modal_close,
      }}
    >
      <h2 className={styles.h2}>{feedbackText}</h2>
      {content}
      {submitButton}
    </Modal>
  );
}
